import React from "react";
import { Link } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";

import { hasValidAdminUnitRouteId } from "../../../pages/admin/uebersicht/uebersichtHelpers";
import { money, pct, selectUnits } from "./performanceSelectors";

/**
 * Every unit of the selected month, with the four figures the contract carries.
 *
 * WHAT THIS REPLACED, AND WHY THE SOURCE CHANGED
 * ----------------------------------------------
 * The panel's old "Einheiten · Auslastung" list read `occupancyApi.units` — a
 * point-in-time room count for TODAY — and rendered a badge per unit
 * (Belegt / Reserviert / Bald frei / Frei). Two problems, and the second one is
 * why it could not simply be restyled:
 *
 *   1. It showed `units.slice(0, 12)`. A portfolio's thirteenth unit was absent
 *      with nothing on screen saying so — the list looked complete.
 *   2. Its axis was not this tab's axis. A today-snapshot badge sitting under a
 *      heading that says "ausgewählter Monat" answers a different question than
 *      every other figure around it, and the two disagree whenever the month
 *      being viewed is not the current one.
 *
 * This list reads `selected.units`: the same month, the same room-day basis and
 * the same franc figures as the KPI cards above it, per unit. Occupancy is the
 * month's room-day rate, not a snapshot.
 *
 * COMPLETE AND IN THE CONTRACT'S ORDER. No slice, no sort, no filter. The
 * contract publishes `units` in neutral order and `selected_ranking` as the
 * business order, deliberately as two projections; re-ranking this list would
 * make a second ranking that nothing on the server stands behind, and a unit at
 * CHF 0 is a real answer rather than a row worth hiding.
 *
 * LAYOUT. One `<li>` per unit, a grid that collapses to a single column on a
 * phone. Each figure keeps a visible caption at every width — an unlabelled
 * column of four numbers is unreadable once the header scrolls away, and on a
 * 390px screen there is no header to scroll away from.
 */

const CARD = "rounded-xl border border-border bg-card p-4 shadow-sm";

function CardShell({ subtitle, children }) {
  return (
    <section
      data-testid="performance-units-card"
      aria-labelledby="performance-units-heading"
      className={CARD}
    >
      <h2
        id="performance-units-heading"
        className="m-0 text-[13px] font-semibold text-card-foreground"
      >
        Einheiten im Detail
      </h2>
      <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">{subtitle}</p>
      {children}
    </section>
  );
}

/** One labelled figure. The caption stays visible at every width. */
function Figure({ label, value }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-[0.4px] text-muted-foreground">{label}</dt>
      <dd className="m-0 mt-0.5 text-[12px] tabular-nums text-card-foreground">{value}</dd>
    </div>
  );
}

export default function PerformanceUnitsCard({
  period,
  stand = "",
  isRange = false,
  loading = false,
}) {
  // Same rule as the ranking: over a window the figures are period sums, so the
  // month wording goes and `stand` carries the period.
  const subtitle = isRange
    ? `Alle Einheiten im Zeitraum${stand}`
    : `Alle Einheiten im ausgewählten Monat${stand}`;

  if (loading) {
    return (
      <CardShell subtitle={subtitle}>
        <div className="mt-3 space-y-2" aria-busy="true">
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
        </div>
      </CardShell>
    );
  }

  const units = selectUnits(period);

  if (units.length === 0) {
    return (
      <CardShell subtitle={subtitle}>
        {/* The empty state names the window it found nothing in. "in diesem
            Monat" under an April–Juni subtitle would tell the reader we looked
            at a month — the one claim this card must never make about a range. */}
        <p className="mt-4 text-[12px] text-muted-foreground">
          {isRange
            ? "Keine Einheiten in diesem Zeitraum."
            : "Keine Einheiten in diesem Monat."}
        </p>
      </CardShell>
    );
  }

  return (
    <CardShell subtitle={subtitle}>
      <ul
        data-testid="performance-units-rows"
        className="mt-3 list-none divide-y divide-border p-0"
      >
        {units.map((unit) => {
          const label = unit.unit_label || "Unbekannte Einheit";
          const linkable = hasValidAdminUnitRouteId({ id: unit.unit_id });
          return (
            <li
              key={unit.unit_id}
              data-testid={`performance-units-row-${unit.unit_id}`}
              className="py-2.5 first:pt-0 last:pb-0"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
                {/* `break-words`, not `truncate`: the label is the row's
                    identity and cutting it off makes two units look alike. */}
                <span className="min-w-0 break-words text-[12px] font-medium text-card-foreground">
                  {linkable ? (
                    <Link
                      to={`/admin/units/${encodeURIComponent(String(unit.unit_id))}`}
                      className="rounded-[4px] transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      {label}
                    </Link>
                  ) : (
                    label
                  )}
                </span>
                <span className="shrink-0 text-[10px] tabular-nums text-muted-foreground">
                  {unit.total_rooms === null || unit.total_rooms === undefined
                    ? "—"
                    : `${unit.total_rooms} Zi.`}
                </span>
              </div>

              <dl className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-1.5 sm:grid-cols-4">
                <Figure label="Auslastung" value={pct(unit.monthly_occupancy_pct)} />
                <Figure label="Umsatz" value={money(unit.expected_recurring_revenue_chf)} />
                <Figure
                  label="Objektergebnis"
                  value={money(unit.operating_result_chf)}
                />
                <Figure label="Objektmarge" value={pct(unit.operating_margin_pct)} />
              </dl>
            </li>
          );
        })}
      </ul>
    </CardShell>
  );
}
